/**
 * A crawled page's URL path, taken apart.
 *
 * A path is text produced by someone else's site, not a Faro value, so reading
 * its shape is interpretation of external input — the one thing that belongs
 * at a boundary. It is done once here and returned as structured data, because
 * seven feature modules had each re-derived "the path without its query" and
 * "how deep is this page" slightly differently, and comparisons between them
 * silently disagreed on trailing slashes.
 */

/** The path with any query string removed. */
export function pathWithoutQuery(path: string): string {
  return path.split("?")[0] ?? path;
}

/**
 * The path's non-empty segments, query removed. `/a/b/` and `/a/b` both give
 * `["a", "b"]`, so two spellings of one page compare equal.
 */
export function pathSegments(path: string): string[] {
  return pathWithoutQuery(path).split("/").filter(Boolean);
}

/** How many segments deep the page sits. The home page is 0. */
export function pathDepth(path: string): number {
  return pathSegments(path).length;
}

/** The page's own segment, or null at the site root. */
export function lastPathSegment(path: string): string | null {
  return pathSegments(path).at(-1) ?? null;
}

/** The containing path, without the page's own segment. */
export function parentPath(path: string): string {
  return pathSegments(path).slice(0, -1).join("/");
}

/**
 * One spelling per page: query and trailing slashes removed, root as "/". Use
 * this as a map key whenever two paths must be treated as the same page.
 */
export function canonicalPath(path: string): string {
  const segments = pathSegments(path);
  return segments.length === 0 ? "/" : `/${segments.join("/")}`;
}

/**
 * The first path segment, which is how the estate groups pages into clusters.
 * The site root has none, so it is its own cluster.
 */
export function firstPathSegment(path: string, rootLabel: string): string {
  return pathSegments(path)[0] ?? rootLabel;
}
